import { useEffect, useState } from 'react'
import type { ISODate } from '@app/shared'
import clsx from 'clsx'

import type { NotificationRecord } from '../../lib/data/types'
import { buzz } from '../shell/motion'
import { MobileSheet } from '../shell/MobileSheet'
import { mailWhen, notifAt, notifIcon, type NotifIcon } from './day'
import { Glyph, type GlyphName } from './glyphs'

// ============================================================================
// BOARD 22 · THE NOTIFICATIONS SHEET — a floating card hung from the top of Day. What LifeOS has
// sent, newest first; tap a row to go where the notification pointed, × to dismiss it.
//
// DISMISSALS HAVE NO SERVER STATE. The notification log is the server's record of what was sent
// and it is not edited from here; a dismissal is this phone hiding a row, kept in localStorage
// (mobile/day/day.ts → readDismissed). The footer says so, because a row that reappears on the
// desktop would otherwise look like a bug.
//
// The gear goes to the phone's notification settings — the one settings screen it has, and the
// job the old Today band's bell had.
// ============================================================================

const ICON: Record<NotifIcon, GlyphName> = { cap: 'cap', bell: 'bell', clock: 'clock', test: 'bell' }

/** Board 22: a row collapses in 280ms, sliding 110% to the right. */
const DISMISS_MS = 280

export function NotifSheet({
  open,
  onClose,
  today,
  rows,
  unread,
  loading,
  error,
  canUndo,
  onDismiss,
  onClearAll,
  onUndo,
  onOpenRow,
  onSettings,
}: {
  open: boolean
  onClose: () => void
  today: ISODate
  rows: NotificationRecord[]
  /** Rows that arrived since the sheet was last opened: the accent disc. */
  unread: ReadonlySet<string>
  loading: boolean
  error: unknown
  canUndo: boolean
  onDismiss: (id: string) => void
  onClearAll: () => void
  onUndo: () => void
  onOpenRow: (row: NotificationRecord) => void
  onSettings: () => void
}) {
  const [leaving, setLeaving] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    if (!open) setLeaving(new Set())
  }, [open])

  const dismiss = (id: string) => {
    buzz(6)
    setLeaving((s) => new Set(s).add(id))
    window.setTimeout(() => onDismiss(id), DISMISS_MS)
  }

  const empty = !loading && !error && rows.length === 0

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      label="Notifications"
      variant="float"
      bodyStyle={{ padding: '18px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div className="flex items-center gap-2 pb-1 pl-[10px] pr-2">
        <span className="text-[24px] font-extrabold">Notifications</span>
        <button
          type="button"
          onClick={onSettings}
          aria-label="Notification settings"
          className="m-tap-round ml-auto flex size-10 items-center justify-center rounded-full bg-m-card"
        >
          <Glyph name="sliders" size={18} />
        </button>
        <button
          type="button"
          onClick={onClearAll}
          disabled={rows.length === 0}
          className="m-tap h-10 flex-none whitespace-nowrap rounded-[20px] bg-m-card px-[14px] text-[13.5px] font-extrabold disabled:opacity-40"
        >
          Clear all
        </button>
      </div>

      {loading && <div aria-hidden className="h-[62px] animate-pulse rounded-[22px] bg-m-card" />}

      {error != null && (
        <p className="rounded-[22px] bg-m-card px-[18px] py-4 text-[13.5px] font-semibold leading-[1.45]">
          What was sent could not be read. {error instanceof Error ? error.message : ''}
        </p>
      )}

      {rows.map((n) => {
        const gone = leaving.has(n.id)
        const hot = unread.has(n.id)
        return (
          <div
            key={n.id}
            className="m-collapse overflow-hidden"
            style={{ maxHeight: gone ? 0 : 90, opacity: gone ? 0 : 1, transform: gone ? 'translateX(110%)' : 'none' }}
          >
            <div className="flex items-center gap-3 rounded-[22px] bg-m-card py-3 pl-[14px] pr-2 text-m-ink">
              <span
                className={clsx(
                  'flex size-9 flex-none items-center justify-center rounded-full',
                  hot ? 'bg-m-accent text-m-on-accent' : 'bg-m-chip text-m-ink',
                )}
              >
                <Glyph name={ICON[notifIcon(n.kind)]} size={17} strokeWidth={2.2} />
              </span>
              <button
                type="button"
                onClick={() => onOpenRow(n)}
                className="m-tap flex min-w-0 flex-1 flex-col gap-[2px] text-left"
              >
                <span className="text-[15px] font-extrabold leading-[1.2]">{n.title}</span>
                <span className="text-[12.5px] font-semibold opacity-[0.72]">
                  {n.body ? `${n.body} · ` : ''}
                  {mailWhen(notifAt(n), today)}
                </span>
              </button>
              <button
                type="button"
                aria-label={`Dismiss: ${n.title}`}
                onClick={() => dismiss(n.id)}
                className="m-tap-round flex size-9 flex-none items-center justify-center rounded-full opacity-60"
              >
                <Glyph name="close" size={14} strokeWidth={2.6} />
              </button>
            </div>
          </div>
        )
      })}

      {empty && (
        <div className="flex flex-col items-center gap-[6px] rounded-[22px] bg-m-card px-[18px] py-[26px] text-center">
          <span className="text-[17px] font-extrabold">You’re all caught up</span>
          <span className="text-[13px] text-m-sub">The status chip on Day goes back to “Synced”.</span>
          {canUndo && (
            <button
              type="button"
              onClick={onUndo}
              className="m-tap mt-1 h-9 rounded-[18px] bg-m-chip px-[14px] text-[13px] font-extrabold"
            >
              Undo
            </button>
          )}
        </div>
      )}

      <span className="px-[10px] pb-[2px] pt-1 text-[12px] text-m-sub">
        Tap to open · × to dismiss · dismissals stay on this phone
      </span>
    </MobileSheet>
  )
}
